import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppAction, AgentApplicationHost } from '../interfaces/app-action';
import { AgentWorkspaceContext } from '../interfaces/agent-session';
import { AgentWorkspaceRegistry } from './agent-workspace-registry.service';
import { isRecord } from './agent-stream';
import { TabType } from '../../enums/TabType';

function validPersistenceIntent(raw: unknown): boolean {
  return isRecord(raw) && (raw['mode'] === 'NONE' && Object.keys(raw).length === 1
    || raw['mode'] === 'SAVE' && Object.keys(raw).length === 1
    || raw['mode'] === 'SAVE_AS' && Object.keys(raw).length === 2
      && typeof raw['name'] === 'string' && !!raw['name'].trim() && raw['name'].length <= 256);
}

function validSimulationWorkflow(raw: unknown, first: Record<string, unknown>): boolean {
  if (!isRecord(raw) || Object.keys(raw).some(key => !['persistenceIntent', 'remaining'].includes(key))
      || !validPersistenceIntent(raw['persistenceIntent']) || !Array.isArray(raw['remaining'])
      || raw['remaining'].length < 1 || raw['remaining'].length > 3) return false;
  let saves = first['type'] === 'SAVE_SIMULATION_CONFIG' ? 1 : 0;
  let runs = first['type'] === 'RUN_SIMULATION' ? 1 : 0;
  let interpretations = 0;
  let interpreted = false;
  let saveStep: Record<string, unknown> | undefined = first['type'] === 'SAVE_SIMULATION_CONFIG'
    ? { mode: first['mode'], ...(first['mode'] === 'SAVE_AS' ? { name: first['simulationName'] } : {}) } : undefined;
  for (const step of raw['remaining']) {
    if (!isRecord(step)) return false;
    if (step['kind'] === 'RUN' && Object.keys(step).length === 1) {
      if (interpreted) return false;
      runs++;
    } else if (step['kind'] === 'INTERPRET' && Object.keys(step).length === 1) {
      interpretations++;
      interpreted = true;
    }
    else if (step['kind'] === 'SAVE' && ['SAVE', 'SAVE_AS'].includes(step['mode'] as string)
        && Object.keys(step).every(key => ['kind', 'mode', 'name'].includes(key))
        && (step['mode'] === 'SAVE' ? !('name' in step)
          : typeof step['name'] === 'string' && !!step['name'].trim() && step['name'].length <= 256)) {
      saves++;
      saveStep = step;
    } else return false;
  }
  if (saves > 1 || runs > 1 || interpretations > 1) return false;
  const intent = raw['persistenceIntent'] as Record<string, unknown>;
  return saves === 0 ? intent['mode'] === 'NONE'
    : intent['mode'] === saveStep?.['mode']
      && (intent['mode'] !== 'SAVE_AS' || intent['name'] === saveStep?.['name']);
}

export function parseAppActions(raw: unknown): AppAction[] {
  if (!Array.isArray(raw) || raw.length > 12) throw new Error('Invalid application action batch');
  for (const action of raw) {
    if (!isRecord(action) || typeof action['actionId'] !== 'string' || !action['actionId']) throw new Error('Invalid action');
    const type = action['type'];
    const fields = type === 'NAVIGATE_ROUTE' ? ['route'] : type === 'ACTIVATE_TAB' || type === 'CLOSE_TAB' ? ['tabInstanceId'] : type === 'OPEN_PANEL' ? ['panel'] : type === 'RESTORE_VIEW' ? ['viewName', 'destination'] : type === 'LOAD_MODEL' ? ['modelName', 'destination'] : type === 'SAVE_VIEW_AS' ? ['viewName'] : type === 'SET_TAB_MODE' ? ['tabType'] : type === 'APPLY_SIMULATION_CONFIG' ? ['modelUuid', 'expectedSimulationRevision', 'configuration'] : type === 'RUN_SIMULATION' ? ['expectedSimulationRevision'] : type === 'SAVE_SIMULATION_CONFIG' ? ['mode', ...(typeof action['simulationName'] === 'string' ? ['simulationName'] : [])] : type === 'LOAD_SIMULATION_CONFIG' ? ['simulationUuid'] : type === 'OPEN_TAB' ? [] : null;
    if (type === 'SET_TAB_MODE' && fields) fields.push('continueSimulation');
    if (type === 'APPLY_SIMULATION_CONFIG' && fields) fields.push('openConfiguration', 'afterApply', 'simulationWorkflow');
    if (type === 'RUN_SIMULATION' && fields) fields.push('simulationWorkflow');
    if (type === 'SAVE_SIMULATION_CONFIG' && fields) fields.push('persistenceIntent', 'simulationWorkflow');
    if (!fields || Object.keys(action).some(key => !['actionId', 'type', ...fields].includes(key))) throw new Error('Unsupported action');
    if (type === 'SET_TAB_MODE' && action['continueSimulation'] !== undefined
        && (typeof action['continueSimulation'] !== 'boolean' || action['tabType'] !== TabType.SIMULATION)) throw new Error('Invalid simulation continuation');
    if (type === 'APPLY_SIMULATION_CONFIG' && action['openConfiguration'] !== undefined
        && typeof action['openConfiguration'] !== 'boolean') throw new Error('Invalid Simulation panel option');
    if (type === 'APPLY_SIMULATION_CONFIG' && action['afterApply'] !== undefined
        && !['RUN', 'RUN_AND_INTERPRET'].includes(action['afterApply'] as string)) throw new Error('Invalid Simulation continuation intent');
    if (action['simulationWorkflow'] !== undefined
        && (!['APPLY_SIMULATION_CONFIG', 'RUN_SIMULATION', 'SAVE_SIMULATION_CONFIG'].includes(type as string)
          || !validSimulationWorkflow(action['simulationWorkflow'], action)
          || action['afterApply'] !== undefined)) throw new Error('Invalid Simulation workflow');
    if (type === 'NAVIGATE_ROUTE' && !['/', '/panel'].includes(action['route'] as string)) throw new Error('Unsupported route');
    if ((type === 'ACTIVATE_TAB' || type === 'CLOSE_TAB') && (typeof action['tabInstanceId'] !== 'string' || !action['tabInstanceId'])) throw new Error('Missing tab');
    if (type === 'OPEN_PANEL' && !['models', 'views', 'schemas', 'taxonomy', 'properties'].includes(action['panel'] as string)) throw new Error('Unsupported panel');
    if (type === 'RESTORE_VIEW' && (typeof action['viewName'] !== 'string' || !action['viewName'].trim() || action['viewName'].length > 256
        || !['CURRENT_CREATOR', 'NEW_TAB'].includes(action['destination'] as string))) throw new Error('Invalid View restore request');
    if (type === 'LOAD_MODEL' && (typeof action['modelName'] !== 'string' || !action['modelName'].trim() || action['modelName'].length > 256
        || !['CURRENT_CREATOR', 'NEW_TAB'].includes(action['destination'] as string))) throw new Error('Invalid Model load request');
    if (type === 'SAVE_VIEW_AS' && (typeof action['viewName'] !== 'string' || !action['viewName'].trim() || action['viewName'].length > 256)) throw new Error('Invalid View save request');
    if (type === 'SET_TAB_MODE' && ![TabType.VIEW, TabType.MODEL_CREATOR, TabType.METRICS, TabType.SIMULATION].includes(action['tabType'] as TabType)) throw new Error('Unsupported tab mode');
    if ((type === 'APPLY_SIMULATION_CONFIG' || type === 'RUN_SIMULATION') && (!Number.isInteger(action['expectedSimulationRevision']) || (type === 'APPLY_SIMULATION_CONFIG' && (typeof action['modelUuid'] !== 'string' || !action['modelUuid'] || !isRecord(action['configuration']))))) throw new Error('Invalid simulation action');
    if (type === 'SAVE_SIMULATION_CONFIG' && (!['SAVE', 'SAVE_AS'].includes(action['mode'] as string)
        || (action['simulationName'] !== undefined && (typeof action['simulationName'] !== 'string' || !action['simulationName'].trim()))
        || (action['mode'] === 'SAVE' && action['simulationName'] !== undefined)
        || !validPersistenceIntent(action['persistenceIntent'])
        || (action['persistenceIntent'] as Record<string, unknown>)['mode'] !== action['mode']
        || (action['mode'] === 'SAVE_AS' && (action['persistenceIntent'] as Record<string, unknown>)['name'] !== action['simulationName']))) throw new Error('Invalid simulation save');
    if (type === 'LOAD_SIMULATION_CONFIG' && (typeof action['simulationUuid'] !== 'string' || !action['simulationUuid'])) throw new Error('Invalid simulation load');
  }
  const actions = structuredClone(raw) as AppAction[];
  if (actions.length > 1 && !actions.every(action => action.type === 'RESTORE_VIEW' && action.destination === 'CURRENT_CREATOR')) {
    throw new Error('Only current-Creator View restores may be batched');
  }
  return actions;
}

@Injectable({ providedIn: 'root' })
export class AgentAppActions {
  private readonly router = inject(Router);
  private readonly registry = inject(AgentWorkspaceRegistry);
  private host?: AgentApplicationHost;
  attach(host: AgentApplicationHost): void { this.host = host; }
  detach(host: AgentApplicationHost): void { if (this.host === host) this.host = undefined; }

  async execute(action: AppAction, scope: AgentWorkspaceContext, expectedAppRevision: number): Promise<void> {
    // A restore intentionally changes the tab topology. Capturing the live Canvas
    // can also advance the app revision without changing the originating tab, so
    // validate that tab's own context instead of rejecting a valid restore.
    const compatibleRestore = action.type === 'RESTORE_VIEW'
      && action.destination === 'CURRENT_CREATOR'
      && this.registry.isCompatibleRestoreTarget(scope);
    if ((!this.registry.isCurrent(scope) && !compatibleRestore)
        || (action.type !== 'RESTORE_VIEW' && this.registry.appRevision() !== expectedAppRevision)) {
      throw new Error('Application changed');
    }
    if (action.type === 'NAVIGATE_ROUTE') {
      if (!await this.router.navigateByUrl(action.route)) throw new Error('Navigation rejected');
      return;
    }
    if (!this.host) throw new Error('Workspace navigation is unavailable on this page');
    if ('tabInstanceId' in action && !this.registry.capture(action.tabInstanceId)) throw new Error('Tab closed');
    switch (action.type) {
      case 'OPEN_TAB': this.host.openTab(); break;
      case 'LOAD_MODEL': await this.host.loadModel(action.modelName, action.destination); break;
      case 'RESTORE_VIEW': await this.host.restoreView(action.viewName, action.destination); break;
      case 'SAVE_VIEW_AS': await this.host.saveViewAs(action.viewName); break;
      case 'SET_TAB_MODE': this.host.setTabMode(scope.tabInstanceId!, action.tabType); break;
      case 'APPLY_SIMULATION_CONFIG': await this.host.applySimulationConfiguration(scope.tabInstanceId!, action.modelUuid, action.expectedSimulationRevision, action.configuration, action.openConfiguration); break;
      case 'RUN_SIMULATION': await this.host.runSimulation(scope.tabInstanceId!, action.expectedSimulationRevision); break;
      case 'SAVE_SIMULATION_CONFIG': await this.host.saveSimulation(scope.tabInstanceId!, action.mode, action.simulationName); break;
      case 'LOAD_SIMULATION_CONFIG': await this.host.loadSimulation(scope.tabInstanceId!, action.simulationUuid); break;
      case 'ACTIVATE_TAB': this.host.activateTab(action.tabInstanceId); break;
      case 'CLOSE_TAB': this.host.closeTab(action.tabInstanceId); break;
      case 'OPEN_PANEL': this.host.openPanel(action.panel); break;
    }
  }

  async executeBatch(actions: readonly AppAction[], scope: AgentWorkspaceContext, expectedAppRevision: number): Promise<void> {
    if (!actions.length) return;
    if (actions.length === 1) return this.execute(actions[0], scope, expectedAppRevision);
    if (!actions.every(action => action.type === 'RESTORE_VIEW' && action.destination === 'CURRENT_CREATOR')
        || (!this.registry.isCurrent(scope) && !this.registry.isCompatibleRestoreTarget(scope))
        || !this.host) throw new Error('Application changed');
    // The batch is validated once against the originating scope. Each restore
    // legitimately advances contextRevision, so revalidating between siblings
    // would reject the second action created by the same trusted response.
    const restores = actions as Extract<AppAction, { type: 'RESTORE_VIEW' }>[];
    for (const action of restores) {
      try {
        await this.host.restoreView(action.viewName, action.destination);
      } catch (error) {
        const reason = error instanceof Error ? error.message : 'Unknown restore error';
        throw new Error(`Could not restore “${action.viewName}”: ${reason}`);
      }
    }
  }
}
